import { formatBusinessDate, formatInstant } from '@ampersandbrands/ui';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Cockpit's date formatters are thin wrappers over the shared helpers in
 * `@ampersandbrands/ui`, so Cockpit, Sweet Spot, and Connect all read an API
 * timestamp the same way: a calendar date renders from its digits and never
 * shifts a day, and an instant is normalised to UTC before it is rendered in
 * the viewer's timezone. The wrappers exist only to keep Cockpit's two
 * display conventions the shared helpers don't have: "--" for a missing value,
 * and echoing the original string back when it can't be parsed.
 */
function orOriginal(formatted: string, original: string): string {
  return formatted || original;
}

/**
 * Formats a YYYY-MM-DD or ISO timestamp as "May 29, 2026".
 * Reads the calendar date out of the string, so a date-only value never
 * shifts to the previous day in a browser west of UTC.
 */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '--';
  const s = String(v).trim();
  return orOriginal(formatBusinessDate(s), s);
}

/**
 * Formats a full ISO timestamp as "May 29, 2026, 2:34 PM" in the user's local
 * timezone. A timestamp with no zone marker is treated as UTC, which is what
 * the API stores.
 */
export function formatDateTime(v: string | null | undefined): string {
  if (!v) return '--';
  const s = String(v).trim();
  return orOriginal(formatInstant(s), s);
}

/**
 * Formats a full ISO timestamp as the viewer's own calendar date: "May 29,
 * 2026". Use for *_at columns (shipped_at, created_at, last_login_at, ...)
 * where the underlying value is an instant, not a calendar date — passing
 * those to formatDate/formatDateShort reads the UTC digits and can render
 * the wrong day for anyone west of UTC.
 */
export function formatInstantDate(v: string | null | undefined): string {
  if (!v) return '--';
  const s = String(v).trim();
  return orOriginal(formatInstant(s, { dateOnly: true }), s);
}

/**
 * Auto-detects format: YYYY-MM-DD inputs use formatDate (date only),
 * anything with a time component uses formatDateTime (date + 12-hour time).
 */
export function formatDateMaybeTime(v: string | null | undefined): string {
  if (!v) return '--';
  return DATE_ONLY_RE.test(String(v).trim()) ? formatDate(v) : formatDateTime(v);
}

/**
 * Compact date for dense grid cells: "05/29/26" (MM/DD/YY).
 * Use this in list/grid views where column width matters.
 * Use formatDate for detail pages where readability takes priority.
 */
export function formatDateShort(v: string | null | undefined): string {
  if (!v) return '--';
  const s = String(v).trim();
  return orOriginal(formatBusinessDate(s, { short: true }), s);
}
